/**
 * Countdown arithmetic, kept pure so the boundary can be tested without a clock.
 *
 * Canon § 8 puts the daily boundary at 00:00 UTC and makes core's clock canonical. The
 * site therefore counts down from `serverTime`, not from `Date.now()` — a visitor whose
 * machine is ten minutes fast must not be told bidding has closed while it is open.
 */

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;

export interface Countdown {
  /** Whole milliseconds remaining, clamped at zero. */
  remainingMs: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** True once the target has passed. The UI must then stop implying time is left. */
  expired: boolean;
}

/**
 * Remaining time between two instants.
 *
 * Never negative: past the target the countdown is `expired` and reads `00:00:00`, because
 * a negative duration on a board is a bug the visitor should never have to interpret.
 * `hours` is not capped at 24 — a period longer than a day counts down as `26:10:00`
 * rather than silently wrapping.
 */
export function countdownBetween(nowMs: number, targetMs: number): Countdown {
  const remainingMs = Math.max(0, targetMs - nowMs);
  return {
    remainingMs,
    hours: Math.floor(remainingMs / HOUR_MS),
    minutes: Math.floor((remainingMs % HOUR_MS) / MINUTE_MS),
    seconds: Math.floor((remainingMs % MINUTE_MS) / SECOND_MS),
    expired: remainingMs === 0,
  };
}

/** `07:41:12`. Zero-padded so the figure does not change width as it ticks. */
export function formatCountdown(countdown: Countdown): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${pad(countdown.hours)}:${pad(countdown.minutes)}:${pad(countdown.seconds)}`;
}

/** Spoken form for assistive technology, which must not hear a per-second tick. */
export function describeCountdown(countdown: Countdown): string {
  if (countdown.expired) return "no time remaining";
  const parts: string[] = [];
  if (countdown.hours > 0) {
    parts.push(`${String(countdown.hours)} hour${countdown.hours === 1 ? "" : "s"}`);
  }
  parts.push(`${String(countdown.minutes)} minute${countdown.minutes === 1 ? "" : "s"}`);
  return parts.join(" ");
}

/**
 * The next 00:00 UTC after the given instant — when today's board is replaced (canon § 8).
 *
 * An instant exactly on midnight counts down a full day rather than returning zero: the
 * board that has just appeared has a whole display day ahead of it.
 */
export function nextUtcMidnight(nowMs: number): number {
  const now = new Date(nowMs);
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
}
